// src/Components/HistoryEachDates/index.js
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Calendar, History, MessageSquare, Trash2 } from 'lucide-react';
import { useChatContext } from '../../Context/ChatContext';
import HistoryDatesModel from '../HistoryDatesModel';
import { api } from '../../Api/BaseApi';
import { useTheme } from '../../Context/ThemeContext';
import './index.css';

// it is used to debounce the function
// it is used to debounce the function to avoid multiple calls
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => { clearTimeout(timeout); func(...args); };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const HistoryEachDates = React.forwardRef((props, ref) => {
  const [chatSessionDates, setChatSessionDates] = useState([]); // Stores { session_date: "YYYY-MM-DD" }
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [selectedDisplayDate, setSelectedDisplayDate] = useState(null); // Date object for UI, or string "YYYY-MM-DD"
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [questionsForDisplay, setQuestionsForDisplay] = useState([]);
  const [authError, setAuthError] = useState('');

  const { isDarkMode } = useTheme();

  // useChatContext is used to get the context messages
  // it is used to get the context loadMessagesForDate
  // it is used to get the context setSelectedQuestion
  // it is used to get the context formatDateString
  // it is used to get the context currentDateBeingViewed
  const {
    messages: contextMessages,
    loadMessagesForDate: contextLoadMessagesForDate, // Tells context to change its view
    setSelectedQuestion: contextSetSelectedQuestion,
    formatDateString,
    currentDateBeingViewed: contextCurrentlyViewedDate,
    userId: contextUserId, // Get userId from context
    isUserIdentified, // Get identification status
    newChatMode, // when true, a fresh blank chat is open - don't auto-load today
  } = useChatContext();


  // it is used to check if the date is today and if it is not invalid date then return true
  const isDateToday = useCallback((date) => {
    if (!date) return false;
    const todayStr = formatDateString(new Date());
    const checkDateStr = typeof date === 'string' ? date : formatDateString(date);
    return todayStr === checkDateStr && todayStr !== "INVALID_DATE";
  }, [formatDateString]);

  // it is used to sync questionsForDisplay when context messages update AND
  // the selectedDisplayDate matches what the context is showing. 
  // means if the selectedDisplayDate is today then sync questionsForDisplay with contextMessages 
  // if the selectedDisplayDate is not today then sync questionsForDisplay with contextMessages
  useEffect(() => {
    if (selectedDisplayDate &&
      contextCurrentlyViewedDate &&
      (typeof selectedDisplayDate === 'string' ? selectedDisplayDate : formatDateString(selectedDisplayDate)) === contextCurrentlyViewedDate) {
      setQuestionsForDisplay(contextMessages || []);
    }
  }, [contextMessages, selectedDisplayDate, contextCurrentlyViewedDate, formatDateString]);


  // When a fresh blank chat is opened (New Chat), blank this component's question
  // list but keep it pointed at today, so the sidebar live-syncs the new chat as
  // the user types (it is empty now, and fills in as messages arrive). The prior
  // chat stays saved per date and is reachable from History.
  useEffect(() => {
    if (newChatMode) {
      setSelectedDisplayDate(new Date());
      setQuestionsForDisplay([]);
    }
  }, [newChatMode]);

  // it is used to fetch chat session dates from the server to display in the modal
  // it is used to debounce the function to avoid multiple calls
  // it is used to update the chat session dates in the modal
  // it is used to handle the error if any 
  const fetchChatSessionDates = useCallback(async () => {

    setAuthError(''); // Clear previous auth errors
    // if (!AuthApi.isAuthenticated()) {
    //   setAuthError("Please log in to view history dates.");
    //   setChatSessionDates([]);
    //   return;
    // }
    if (!isUserIdentified || !contextUserId) { // MODIFIED: Check contextUserId
      setAuthError("Please log in and identify to view history dates.");
      setChatSessionDates([]);
      return;
    }


    try {
      // Use FastAPI proxy instance
      const response = await api.get(`/auth/api/chat/history/dates/${contextUserId}`, { withCredentials: true });
      // Axios puts data directly in response.data
      const responseData = response.data;




      if (responseData.success && Array.isArray(responseData.data)) {

        // `responseData.data` is like ['2025-05-29', '2025-05-28']
        const processedDates = responseData.data.map(dateString => { // `dateString` is "2025-05-29"
          const dateObj = new Date(dateString); // Parse the string directly
          const formatted = formatDateString(dateObj);
          return { session_date: formatted }; // Create the object structure you need
        }).filter(d => d.session_date !== "INVALID_DATE");

        const sorted = processedDates.sort((a, b) => new Date(b.session_date) - new Date(a.session_date));

        setChatSessionDates(sorted);

      } else {
        setChatSessionDates([]);
        console.warn("Failed to fetch chat session dates or data format incorrect:", responseData.message);
      }
    } catch (error) {
      console.error('Error fetching chat session dates:', error.response?.data?.message || error.message);
      setChatSessionDates([]);
    }
  }, [formatDateString, contextUserId, isUserIdentified]); // formatDateString is stable 



  // it is used to debounce the function to avoid multiple calls
  // it is used to create a memoized version of the debounce function
  // memoized means it will not be re-created on every render
  const debounceFn = useCallback(
    (fn) => debounce(fn, 600),
    [] // Empty dependency array since debounce itself never changes
  );

  // Create the debounced fetch function
  // it is used to debounce the function to avoid multiple calls
  // it is used to create a memoized version of the debounce function
  // memoized means it will not be re-created on every render
  // it is used to update the chat session dates in the modal
  // it is used to handle the error if any 
  const debouncedFetchChatSessionDates = useMemo(
    () => {
      return debounceFn(fetchChatSessionDates);
    },
    [debounceFn, fetchChatSessionDates]
  );

  // Function to load and update questions for a date
  // it is used to load and update questions for a date
  // it is used to update the questionsForDisplay state
  // it is used to update the selectedDisplayDate state
  // it is used to handle the error if any 
  const loadAndUpdateQuestions = useCallback(async (date) => {
    setAuthError('');

    const dateStr = formatDateString(date);
    setIsLocalLoading(true);
    try {
      // Load messages for this date
      await contextLoadMessagesForDate(dateStr);
      // Update questions display
      setQuestionsForDisplay(contextMessages || []);
      setSelectedDisplayDate(date);
    } catch (error) {
      error('Error loading messages:', error);
      setQuestionsForDisplay([]);
    } finally {
      setIsLocalLoading(false);
    }
  }, [contextLoadMessagesForDate, contextMessages, formatDateString, setQuestionsForDisplay, setSelectedDisplayDate]);

  // Expose functions to parent components through ref
  // it is used to expose functions to parent components through ref
  // it is used to update the chat session dates in the modal
  // it is used to update the history for a specific date
  // it is used to handle the error if any 
  React.useImperativeHandle(ref, () => ({
    fetchChatSessionDates,
    updateHistoryForDate: async (date) => {
      // Load and update questions
      await loadAndUpdateQuestions(date);
      // Refresh the dates list
      await fetchChatSessionDates();
    }
  }));

  // Load history for a specific date to display in this component
  // This will also update context messages for the main chat view.
  // Note: This does not change contextCurrentlyViewedDate, that is handled by the context.
  // it is used to load history for a specific date to display in this component
  // it is used to update the questionsForDisplay state
  // it is used to update the selectedDisplayDate state
  // it is used to handle the error if any 
  // it is used to update the context messages for the main chat view
  // it is used to update the local loading state
  const loadHistoryForSelectedDate = useCallback(async (dateToLoad) => {
    setAuthError('');

    if (!isUserIdentified || !contextUserId) { // MODIFIED: Check contextUserId
      setQuestionsForDisplay([]);
      // warn("User not identified, cannot load history.");
      return;
    }

    if (!dateToLoad) { setQuestionsForDisplay([]); return; }
    const dateStrToLoad = typeof dateToLoad === 'string' ? dateToLoad : formatDateString(dateToLoad);

    if (dateStrToLoad === "INVALID_DATE") {
      setQuestionsForDisplay([]);
      console.warn("Attempted to load history for an invalid date.");
      return;
    }

    // it is used to update the local loading state
    // it is used to update the selectedDisplayDate state
    setIsLocalLoading(true);
    setSelectedDisplayDate(dateToLoad); // Update which date this component thinks is selected

    // Tell context to load messages for this date.
    // The useEffect above will sync questionsForDisplay once context updates.
    // it is used to update the context messages for the main chat view
    // it is used to update the local loading state
    await contextLoadMessagesForDate(dateStrToLoad);
    setIsLocalLoading(false); // Context's isLoading will handle ResponseContainer, this is for local display

  }, [formatDateString, contextLoadMessagesForDate, contextUserId, isUserIdentified]);

  // Initial load of chat session dates and today's messages
  // This runs once on mount to populate the modal and set today's messages.
  // It uses the debounced function to avoid multiple calls if the component re-renders.
  // Note: This does not set selectedDisplayDate, that is done in the effect below.


  useEffect(() => {
    if (isUserIdentified && contextUserId) { // MODIFIED: Check contextUserId
      debouncedFetchChatSessionDates();
      const today = new Date();
      // Only auto-load today if no date is actively selected AND we are not in a
      // fresh blank chat (New Chat). Otherwise we'd repopulate the blank view.
      if (!selectedDisplayDate && !newChatMode) {
        loadHistoryForSelectedDate(today);
      }
    } else {
      setAuthError("Please log in to view chat history.");
      setQuestionsForDisplay([]); // Clear questions if not authenticated
      setChatSessionDates([]); // Clear dates if not authenticated
    }
    // eslint-disable-next-line
  }, [isUserIdentified, contextUserId]); // Only on mount

  // useEffect(() => { // Initial load
  //   debouncedFetchChatSessionDates();
  //   const today = new Date();
  //   // Select today and trigger context to load today's messages (which it does on init anyway)
  //   // This ensures selectedDisplayDate is set.
  //   loadHistoryForSelectedDate(today);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  //   }, []); // Only on mount

  // Effect to handle when the modal opens
  // This will fetch the latest chat session dates when the modal is opened.
  const handleDateClickFromModal = useCallback((dateString) => { // dateString is "YYYY-MM-DD"
    const newSelectedDate = new Date(dateString + "T00:00:00"); // Ensure it's parsed as local date
    loadHistoryForSelectedDate(newSelectedDate);
    // setIsModalOpen(false); // Modal closes itself now
  }, [loadHistoryForSelectedDate]);

  // delete a specific question
  // This is called when the delete button is clicked on a question.
  // It will optimistically update the UI and then call the API to delete the message.
  // If the API fails, it will revert the UI to the original state.
  // Note: This assumes that the messageToDelete has a history_id if it's a saved message.
  const handleDeleteQuestion = async (messageToDelete) => {
    setAuthError(''); // Clear previous auth errors
    // if (!AuthApi.isAuthenticated()) {
    //   setAuthError("Please log in to delete messages.");
    //   return;
    // }
    if (!isUserIdentified || !contextUserId) { // MODIFIED: Check contextUserId
      setAuthError("Please log in to delete messages.");
      return;
    }

    // messageToDelete should have history_id if it's a saved message
    if (!messageToDelete.history_id || !selectedDisplayDate) {
      alert("Cannot delete: Message ID or selected date is missing.");
      return;
    }
    // Confirm deletion
    // Note: This is an optimistic update, we will revert if server fails.
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    // Optimistically update UI by removing the question immediately
    // by removing the question from the questionsForDisplay array
    // it is used to update the questionsForDisplay state
    const originalQuestions = [...questionsForDisplay];
    setQuestionsForDisplay(prev => prev.filter(q => q.history_id !== messageToDelete.history_id));
    try {
      // it is used to delete the message from the server
      const response = await api.delete(`/auth/api/chat/message/${messageToDelete.history_id}`, { withCredentials: true });

      // Check if the API call was successful
      if (response.data && response.data.success) {
        // Refresh the view for the currently selectedDisplayDate
        const currentSelectedDateStr = typeof selectedDisplayDate === 'string' ? selectedDisplayDate : formatDateString(selectedDisplayDate);
        // contextLoadMessagesForDate is used to update the context messages for the main chat view
        // currentSelectedDateStr is used to update the selectedDisplayDate state by converting the selectedDisplayDate to a string and then loading the messages for that date
        // it is used to update the context messages for the main chat view
        await contextLoadMessagesForDate(currentSelectedDateStr);
        debouncedFetchChatSessionDates(); // Refresh list of dates in modal
      } else {
        throw new Error(response.data?.message || 'Failed to delete message');
      }
    } catch (error) {
      error('Error deleting question:', error);
      alert('Failed to delete message. Reverting UI.');
      setQuestionsForDisplay(originalQuestions); // Revert optimistic update
    }
  };

  // delete all messages for a specific date
  // This is called when the delete button is clicked on the date display.
  // It will confirm with the user and then call the API to delete all messages for that date.
  // If the date being deleted is the one currently displayed, it will switch to today.
  const handleDeleteDate = async (dateObjToDelete) => {
    setAuthError(''); // Clear previous auth errors
    // if (!AuthApi.isAuthenticated()) {
    //   setAuthError("Please log in to delete history.");
    //   return;
    // }
    if (!isUserIdentified || !contextUserId) { // MODIFIED: Check contextUserId
      setAuthError("Please log in to delete history.");
      return;
    }

    if (!dateObjToDelete) return;
    const dateStrToDelete = formatDateString(dateObjToDelete);
    if (dateStrToDelete === "INVALID_DATE") return;

    setIsLocalLoading(true);
    try {
      // delete date from the server
      const response = await api.delete(`/auth/api/chat/history/${dateStrToDelete}?userId=${contextUserId}`, { withCredentials: true });
      if (response.status >= 200 && response.status < 300) { // General check for successful HTTP status

        // get today's date
        // it is used to get today's date
        // it is used to update the selectedDisplayDate state by converting the selectedDisplayDate to a string and then loading the messages for that date
        const today = new Date();
        const todayStr = formatDateString(today);

        // Only update the dates list if we're not deleting today's date
        if (dateStrToDelete !== todayStr) {
          setChatSessionDates(prev => prev.filter(d => d.session_date !== dateStrToDelete));
        } else {
          // If it's today's date, we keep it but clear the messages
          setQuestionsForDisplay([]);
        }

        // it is used to refresh the dates list from the server
        await fetchChatSessionDates(); // Using direct call instead of debounced version

        // If the deleted date was the one being displayed locally in this component
        if (selectedDisplayDate && formatDateString(selectedDisplayDate) === dateStrToDelete) {
          // Switch local display and context view to today
          // it is used to update the selectedDisplayDate state by converting the selectedDisplayDate to a string and then loading the messages for that date
          // it is used to update the context messages for the main chat view
          // it is used to update the questionsForDisplay state
          setSelectedDisplayDate(today);
          await contextLoadMessagesForDate(todayStr); // This updates context, which should clear ResponseContainer
          // And also questionsForDisplay via useEffect
        }
        // Else if the context was viewing the deleted date (but this component was showing something else)
        else if (contextCurrentlyViewedDate === dateStrToDelete) {
          // Switch context view to today
          // it is used to update the context messages for the main chat view
          await contextLoadMessagesForDate(todayStr);
          // selectedDisplayDate in this component remains unchanged, but context changed.
          // If selectedDisplayDate is now out of sync with context, the useEffect might not update questionsForDisplay.
        }
        // If a completely different date was deleted, only the chatSessionDates list changes.
        // The current view (local and context) should be unaffected.
      } else {
        // Handle cases where backend might return 200 OK but with { success: false }
        throw new Error(response.data?.message || 'Server indicated failure to delete date history');
      }
      // if (!response.ok) throw new Error('Server failed to delete date history');

      // setChatSessionDates(prev => prev.filter(d => d.session_date !== dateStrToDelete));
      // // If the deleted date was the one being displayed here or in context:
      // if (selectedDisplayDate && (typeof selectedDisplayDate === 'string' ? selectedDisplayDate : formatDateString(selectedDisplayDate)) === dateStrToDelete) {
      //   const today = new Date();
      //   setSelectedDisplayDate(today); // Switch to today
      //   await contextLoadMessagesForDate(formatDateString(today)); // Tell context to load today
      // } else if (contextCurrentlyViewedDate === dateStrToDelete) {
      //   // If context was viewing the deleted date, switch it to today
      //   await contextLoadMessagesForDate(formatDateString(new Date()));
      // }
      // If just a non-selected, non-context-viewed date was deleted, only session dates list changes.
    } catch (error) {
      error('Error deleting date history:', error);
      if (error.response?.status === 401) {
        setAuthError("Session expired. Please log in again.");
      }
    } finally {
      setIsLocalLoading(false);
    }
  };

  // it is used to get the current display date string
  const currentDisplayDateStr = selectedDisplayDate ? (typeof selectedDisplayDate === 'string' ? selectedDisplayDate : formatDateString(selectedDisplayDate)) : null;

  return (
    <div className={`chat-history-dates ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Header with icon and button to open modal */}
      <div className="history-header">
        <History size={18} className="history-icon" />
        <span>Chat History</span>
        <button onClick={() => {
          if (isUserIdentified && contextUserId) { // MODIFIED
            setIsModalOpen(true);
            debouncedFetchChatSessionDates();
          } else {
            setAuthError("Please log in to view history dates.");
          }

        }} className="view-all-btn" aria-label="View all chat dates" title={`View all past dates`}>...</button>
      </div>
      <HistoryDatesModel isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} dates={chatSessionDates} onDateSelect={handleDateClickFromModal} />

      {authError && <div className="history-auth-error">{authError}</div>}

      {/* Display selected date and delete button */}
      {!authError && currentDisplayDateStr && currentDisplayDateStr !== "INVALID_DATE" && (
        <div className="selected-date-display" aria-label="Selected date" title={`Selected date: ${new Date(currentDisplayDateStr + "T00:00:00").toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`}>
          <Calendar size={16} className="calendar-icon" />
          <span className="date-text">
            {new Date(currentDisplayDateStr + "T00:00:00").toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            {isDateToday(currentDisplayDateStr) && " (Today)"}
          </span>
          <button className="delete-date-btn" onClick={() => handleDeleteDate(selectedDisplayDate)} title={`Delete all messages for this date: ${new Date(currentDisplayDateStr + "T00:00:00").toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`}>
            <Trash2 size={14} />
          </button>
        </div>
      )}
      {/* Display questions for the selected date */}
      {!authError && (isLocalLoading && questionsForDisplay.length === 0 ? (
        <div className="history-loading-state">Loading history...</div>
      ) : questionsForDisplay.length > 0 ? (
        <div className="questions-list-scroll-container">
          <ul className="questions-list">
            {questionsForDisplay.map((item) => (
              <li
                key={item.history_id || item.id} // Prefer history_id if available
                className={`question-item ${!isDateToday(currentDisplayDateStr) ? 'disabled' : ''}`}
                onClick={() => {
                  if (isDateToday(currentDisplayDateStr)) {
                    contextSetSelectedQuestion(item.id); // Only allow navigation for today's date
                  }
                }}
                title={`View chat: "${item.question}"`}
              >
                <div className="question-item-content">
                  <MessageSquare size={15} className="question-icon" />
                  <span className="question-text-preview">
                    {item.question && typeof item.question === 'string'
                      ? item.question.length > 10
                        ? `${item.question.substring(0, 10)}...`
                        : item.question
                      : 'No question text'}
                  </span>

                </div>
                {/* {<span className="question-time">
                    {new Date(item.timestamp).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}
                  </span>} */}
                {item.history_id && ( // Only allow delete if it has a history_id (meaning it's saved)
                  <button
                    className="delete-question-btn"
                    onClick={(e) => { e.stopPropagation(); handleDeleteQuestion(item); }}
                    title="Delete this message"
                    aria-label="Delete this message"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>) : currentDisplayDateStr && currentDisplayDateStr !== "INVALID_DATE" ? (
          <div className="no-history-message">No chat history for this date.</div>
        ) : !currentDisplayDateStr && !authError && isUserIdentified ? (
          <div className="no-history-message">Select a date to view history.</div>) : null
      )}
    </div>
  );
});

export default React.memo(HistoryEachDates);